import clsx from 'clsx';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Switch } from '@doger/ui/components/ui/switch';

import {
  type CookiePreferences,
  DEFAULT_PREFERENCE,
  useCookiePreferences,
} from '../cookies/cookie-preferences.hook';
import styles from './cookie-settings.module.scss';

type CookieSettingsProps = {
  className?: string;
  onPreferencesChange?: (preferences: CookiePreferences) => void;
};

type CookieSettingProps = {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
};

export const CookieSetting = memo(
  ({
    label,
    description,
    checked,
    onChange,
    disabled = false,
  }: CookieSettingProps) => (
    <div className={styles.setting}>
      <div className={styles.settingLabel}>
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          disabled={disabled}
        />
        <span className={styles.settingText}>
          <strong>{label}</strong>
          <br />
          {description}
        </span>
      </div>
    </div>
  )
);

CookieSetting.displayName = 'CookieSetting';

export const CookieSettings = memo(
  ({ className, onPreferencesChange }: CookieSettingsProps) => {
    const { t } = useTranslation('core');
    const { settings, updateAnalytics, updateMarketing } = useCookiePreferences(
      {
        onAlreadySet: onPreferencesChange,
        onSaved: onPreferencesChange,
      }
    );

    return (
      <div className={clsx(styles.container, className)}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('cookies.manage.title')}</h3>
        </div>

        <div className={styles.content}>
          <div className={styles.description}>
            {t('cookies.manage.necessaryDescription')}
          </div>

          <div className={styles.description}>
            {t('cookies.manage.optionalDescription')}
          </div>

          <div className={styles.settings}>
            <CookieSetting
              label={t('cookies.manage.analyticsLabel')}
              description={t('cookies.manage.analyticsDescription')}
              checked={settings?.settings?.analytics ?? DEFAULT_PREFERENCE}
              onChange={(checked) => updateAnalytics(checked, { save: true })}
            />

            <CookieSetting
              label={t('cookies.manage.marketingLabel')}
              description={t('cookies.manage.marketingDescription')}
              checked={settings?.settings?.marketing ?? DEFAULT_PREFERENCE}
              onChange={(checked) => updateMarketing(checked, { save: true })}
            />
          </div>
        </div>
      </div>
    );
  }
);

CookieSettings.displayName = 'CookieSettings';
